import { WEEKDAY_DELIVERY_SLOTS, SUNDAY_DELIVERY_SLOTS, deliveryScheduleError, warsawCheckoutTime } from '@workspace/url-source/checkout';
import { CmsEditorialText } from '@/components/cms/CmsEditorialText';
import { CmsSchema } from '@/cms/content';
import { useCmsFaq } from '@/components/cms/useCmsFaq';
import { useState, useEffect, useRef, lazy, Suspense, useId } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Truck, Clock, Calendar, MapPin, Package, Phone, Gift, Store,
  AlertCircle, CheckCircle2, ChevronDown,
  ArrowRight, User, Info, Shield, MessageCircle, HelpCircle, Plus, Minus,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { pl } from 'date-fns/locale';
const DeliveryCalendar = lazy(() =>
  import('@/components/ui/DeliveryCalendar').then(m => ({ default: m.DeliveryCalendar }))
);
import { Footer } from '@/components/sections/Footer';
import { useSeo } from '@/lib/useSeo';
import { PAGE_SEO } from '@/data/pageSeoConfig';
import { SectionLabel } from '@/components/ui/SectionLabel';
import { PageBreadcrumb } from '@/components/sections/PageBreadcrumb';
import { getPolishHolidays } from '@/lib/polishHolidays';
import { usePrerenderReady } from '@/hooks/usePrerenderReady';
import { DOSTAWA_CONTENT } from '@/data/dostawaContent';
import type { FaqEntry } from '@/data/dostawaContent';

/* ─── Tokens ─────────────────────────────────────────────────── */
const GOLD   = '#e05590';
const DARK   = '#2a1f14';
const MUTED  = 'rgba(42,31,20,0.48)';
const LINE   = 'rgba(210,60,120,0.18)';
const CREAM  = '#fff';
const CREAM2 = '#fff';
const CREAM3 = '#fff';
const BROWN  = '#5a3e28';
const BASE   = import.meta.env.BASE_URL.replace(/\/$/, '');

const ease = [0.22, 1, 0.36, 1] as const;
const fadeUp = (delay = 0) => ({
  initial:     { opacity: 0, y: 22 },
  whileInView: { opacity: 1, y: 0 },
  viewport:    { once: true, margin: '-40px' },
  transition:  { duration: 0.55, delay, ease },
});

/* ─── Slots — identyczne jak CheckoutDeliveryForm ─────────────── */
const WEEKDAY_SLOTS = WEEKDAY_DELIVERY_SLOTS.filter(slot => slot.value !== '9-19');
const SUNDAY_SLOTS = SUNDAY_DELIVERY_SLOTS;

function isSundayDate(dateISO: string) {
  const [y, m, d] = dateISO.split('-').map(Number);
  return new Date(y, m - 1, d).getDay() === 0;
}

function getSlotsForDate(dateISO: string) {
  return (isSundayDate(dateISO) ? SUNDAY_SLOTS : WEEKDAY_SLOTS).filter(slot => !deliveryScheduleError(dateISO, slot.value, false));
}

function getHolidayDates(): Date[] {
  const y = new Date().getFullYear();
  return [...getPolishHolidays(y), ...getPolishHolidays(y + 1)];
}

function todayISO() { return warsawCheckoutTime().date; }

/* ─── Shared UI ──────────────────────────────────────────────── */
const BULLET_ICON_MAP: Record<string, LucideIcon> = { clock: Clock, truck: Truck, mapPin: MapPin };


/* ─── 1. HERO ────────────────────────────────────────────────── */
function Hero() {
  return (
    <section style={{ position: 'relative', minHeight: 'calc(52dvh - 45px)', display: 'flex', alignItems: 'center', background: '#fff', overflow: 'hidden' }}>
      <PageBreadcrumb crumbs={[{ label: 'Dostawa i płatność' }]} />

      <div style={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: 1370, margin: '0 auto', padding: 'calc(var(--page-breadcrumb-height, 44px) + 12px) var(--page-gutter) 24px' }}>
        <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease }}
          style={{ maxWidth: 720, margin: '0 auto', textAlign: 'center' }}>



          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35, duration: 0.7, ease }}
            style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: 'clamp(1.1rem, 2vw, 1.4rem)', fontWeight: 400, color: BROWN, marginBottom: 16, fontStyle: 'italic' }}>
            <CmsEditorialText id="information.hero.tagline" value={DOSTAWA_CONTENT.hero.tagline} section="Wstęp" label="Nadtytuł" />
          </motion.p>

          <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.8, ease }}
            style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: 'clamp(2.2rem, 5.9vw, 5.4rem)', fontWeight: 400, lineHeight: 1.0, color: DARK, margin: '0 0 28px' }}>
            <CmsEditorialText id="information.hero.headingLine1" value={DOSTAWA_CONTENT.hero.headingLine1} section="Wstęp" label="Nagłówek — pierwsza linia" /><br />{' '}<em style={{ color: GOLD, fontStyle: 'italic' }}><CmsEditorialText id="information.hero.headingAccent" value={DOSTAWA_CONTENT.hero.headingAccent} section="Wstęp" label="Wyróżniona część nagłówka" /></em>
          </motion.h1>

          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6, duration: 0.8 }}
            style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 'clamp(14px, 1.6vw, 17px)', color: BROWN, lineHeight: 1.85, maxWidth: 540, margin: '0 auto 44px' }}>
            <CmsEditorialText id="information.hero.body" value={DOSTAWA_CONTENT.hero.body} section="Wstęp" label="Opis" multiline />
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.75, duration: 0.7, ease }}
            style={{ display: 'flex', gap: 16, flexWrap: 'wrap', justifyContent: 'center' }}>
            <a href="#dostawa"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 10, fontFamily: 'DM Sans, sans-serif', fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#fff', textDecoration: 'none', background: DARK, padding: '15px 32px', transition: 'opacity 0.2s', cursor: 'pointer' }}
              onMouseEnter={e => ((e.currentTarget as HTMLAnchorElement).style.opacity = '0.85')}
              onMouseLeave={e => ((e.currentTarget as HTMLAnchorElement).style.opacity = '1')}
            >
              <Truck size={13} />
              <CmsEditorialText id="information.hero.ctaInfoLabel" value={DOSTAWA_CONTENT.hero.ctaInfoLabel} section="Wstęp" label="Tekst przycisku" />
            </a>
            <a href="#platnosci"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 10, fontFamily: 'DM Sans, sans-serif', fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', color: DARK, textDecoration: 'none', border: `1px solid ${GOLD}88`, padding: '15px 32px', transition: 'border-color 0.2s', cursor: 'pointer' }}
              onMouseEnter={e => ((e.currentTarget as HTMLAnchorElement).style.borderColor = GOLD)}
              onMouseLeave={e => ((e.currentTarget as HTMLAnchorElement).style.borderColor = `${GOLD}88`)}
            >
              <CmsEditorialText id="information.hero.ctaPaymentLabel" value={DOSTAWA_CONTENT.hero.ctaPaymentLabel} section="Wstęp" label="Tekst przycisku" />
              <ArrowRight size={13} />
            </a>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

/* ─── 2. ZAPLANUJ DOSTAWĘ ────────────────────────────────────── */
function tomorrowISO() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().split('T')[0];
}

function AddressChecker() {
  const [date,    setDate]    = useState(tomorrowISO());
  const [slot,    setSlot]    = useState('9-11');
  const [calOpen, setCalOpen] = useState(false);
  const calRef = useRef<HTMLDivElement>(null);

  // Close calendar on outside click
  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (calRef.current && !calRef.current.contains(e.target as Node)) setCalOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  // When date changes, reset slot to first available for that day
  useEffect(() => {
    const slots = getSlotsForDate(date);
    if (!slots.find(s => s.value === slot)) {
      setSlot(slots[0]?.value ?? '');
    }
  }, [date]); // eslint-disable-line react-hooks/exhaustive-deps

  const isSunday   = isSundayDate(date);
  const currentSlots = getSlotsForDate(date);
  const isLateSlot = slot === '19-21';

  const inputBase: React.CSSProperties = {
    fontFamily: 'DM Sans, sans-serif', fontSize: 14,
    border: `1px solid ${LINE}`, background: '#fff',
    padding: '13px 14px', outline: 'none', color: DARK,
    width: '100%', boxSizing: 'border-box', minHeight: 48,
  };

  return (
    <section style={{ background: '#fff', padding: 'clamp(56px, 7vw, 88px) 0' }}>
      <div style={{ maxWidth: 1370, margin: '0 auto', padding: '0 var(--page-gutter)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 460px), 1fr))', gap: 'clamp(32px, 6vw, 88px)', alignItems: 'start' }}>

          {/* ── Left: info ── */}
          <motion.div {...fadeUp()} style={{ textAlign: 'center' }}>
            <SectionLabel text={DOSTAWA_CONTENT.addressChecker.eyebrow} />
            <h2 style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: 'clamp(2rem, 3.5vw, 3.2rem)', fontWeight: 400, color: DARK, margin: '0 0 16px', lineHeight: 1.1 }}>
              {DOSTAWA_CONTENT.addressChecker.heading}
            </h2>
            <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 14, color: MUTED, lineHeight: 1.85, margin: '0 0 28px' }}>
              {DOSTAWA_CONTENT.addressChecker.body}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'center' }}>
              {DOSTAWA_CONTENT.addressChecker.bullets.map(({ iconKey, text }) => {
                const Icon = BULLET_ICON_MAP[iconKey];
                return (
                  <div key={text} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Icon size={14} color={GOLD} style={{ flexShrink: 0 }} />
                    <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 13, color: MUTED }}>{text}</span>
                  </div>
                );
              })}
            </div>
          </motion.div>

          {/* ── Right: form ── */}
          <motion.div {...fadeUp(0.1)} style={{ background: '#fff', border: `1px solid ${LINE}`, padding: '28px 24px' }}>

            {/* Data dostawy — collapsible calendar */}
            <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 9.5, letterSpacing: '0.2em', textTransform: 'uppercase', color: MUTED, margin: '0 0 8px', display: 'flex', alignItems: 'center', gap: 5 }}>
              <Calendar size={10} />{DOSTAWA_CONTENT.addressChecker.dateLabel}
            </p>
            <div ref={calRef} style={{ position: 'relative', marginBottom: 20 }}>
              <button
                onClick={() => setCalOpen(v => !v)}
                style={{
                  ...inputBase,
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  cursor: 'pointer', textAlign: 'left', gap: 8,
                  border: `1.5px solid ${calOpen ? GOLD : LINE}`,
                  background: calOpen ? `${GOLD}06` : '#fff',
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Calendar size={14} color={GOLD} style={{ flexShrink: 0 }} />
                  <span>
                    {format(parseISO(date), 'eeee, d MMMM yyyy', { locale: pl })}
                    {isSunday && (
                      <span style={{ marginLeft: 8, fontFamily: 'DM Sans, sans-serif', fontSize: 11, color: GOLD }}>
                        {DOSTAWA_CONTENT.addressChecker.sundayBadge}
                      </span>
                    )}
                  </span>
                </span>
                <ChevronDown size={12} color={MUTED} style={{ flexShrink: 0, transition: 'transform 0.2s', transform: calOpen ? 'rotate(180deg)' : 'none' }} />
              </button>
              <AnimatePresence>
                {calOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.18 }}
                    style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 30, marginTop: 4 }}
                  >
                    <Suspense fallback={null}>
                      <DeliveryCalendar
                        value={date}
                        onChange={v => { setDate(v); setCalOpen(false); }}
                        minDate={todayISO()}
                        disabledDates={getHolidayDates()}
                      />
                    </Suspense>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Godzina dostawy */}
            <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 9.5, letterSpacing: '0.2em', textTransform: 'uppercase', color: MUTED, margin: '0 0 8px', display: 'flex', alignItems: 'center', gap: 5 }}>
              <Clock size={10} />{DOSTAWA_CONTENT.addressChecker.timeLabel}
              {isSunday && <span style={{ marginLeft: 6, color: GOLD, fontSize: 9, letterSpacing: '0.12em' }}>{DOSTAWA_CONTENT.addressChecker.sundaySuffix}</span>}
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 20 }}>
              {currentSlots.map(s => {
                const selected = slot === s.value;
                return (
                  <button
                    key={s.value}
                    onClick={() => setSlot(s.value)}
                    style={{
                      padding: '12px 12px', cursor: 'pointer', textAlign: 'center', minHeight: 44,
                      border: `1.5px solid ${selected ? GOLD : LINE}`,
                      background: selected ? `${GOLD}0a` : '#fff',
                      transition: 'all 0.15s',
                    }}
                  >
                    <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 13, color: DARK, display: 'block' }}>
                      {s.label}
                    </span>
                    {(s.surcharge ?? 0) > 0 && (
                      <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 10, color: GOLD }}>
                        +{s.surcharge} zł
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Dynamic info box */}
            <AnimatePresence>
              {(isSunday || isLateSlot) && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.2 }}
                  style={{ overflow: 'hidden', marginBottom: 16 }}
                >
                  <div style={{ padding: '12px 14px', background: `${GOLD}0d`, border: `1px solid ${GOLD}44`, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                    <Info size={14} color={GOLD} style={{ flexShrink: 0, marginTop: 1 }} />
                    <div style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 12, color: DARK, lineHeight: 1.6 }}>
                      {isSunday && (
                        <p style={{ margin: isLateSlot ? '0 0 4px' : 0 }}>
                          Dostawa w <strong>niedzielę</strong> jest realizowana w wybranych przedziałach godzinowych i kosztuje od <strong>40 zł</strong>.
                        </p>
                      )}
                      {isLateSlot && (
                        <p style={{ margin: 0 }}>
                          Dostawa wieczorna <strong>19:00–21:00</strong> wiąże się z dopłatą <strong>+20 zł</strong>.
                        </p>
                      )}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Footer note */}
            <div style={{ padding: '14px 16px', background: '#fff', borderTop: `1px solid ${LINE}` }}>
              <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 12, color: MUTED, margin: 0, lineHeight: 1.65 }}>
                {DOSTAWA_CONTENT.addressChecker.footerNote}
              </p>
            </div>
          </motion.div>

        </div>
      </div>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </section>
  );
}

/* ─── 3. KIEDY DOSTARCZAMY ───────────────────────────────────── */
/* ─── 4. TEN SAM DZIEŃ ───────────────────────────────────────── */
function SameDaySection() {
  return (
    <section id="dostawa" style={{ background: '#fff', padding: 'clamp(56px, 7vw, 88px) 0' }}>
      <div style={{ maxWidth: 1370, margin: '0 auto', padding: '0 var(--page-gutter)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 460px), 1fr))', gap: 'clamp(32px, 6vw, 80px)', alignItems: 'center' }}>
          <motion.div {...fadeUp()} style={{ textAlign: 'center' }}>
            <SectionLabel text={DOSTAWA_CONTENT.sameDay.eyebrow} />
            <h2 style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: 'clamp(2rem, 3.5vw, 3rem)', fontWeight: 400, color: DARK, margin: '0 0 18px', lineHeight: 1.1 }}>
              <CmsEditorialText id="information.sameDay.heading" value={DOSTAWA_CONTENT.sameDay.heading} section="Dostawa tego samego dnia" label="Nagłówek" />
            </h2>
            <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 14, color: MUTED, lineHeight: 1.85, marginBottom: 28 }}>
              <CmsEditorialText id="information.sameDay.body" value={DOSTAWA_CONTENT.sameDay.body} section="Dostawa tego samego dnia" label="Opis terminu i kosztu" multiline />
            </p>
            <h3 style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: 'clamp(1.3rem, 2vw, 1.75rem)', fontWeight: 400, color: DARK, margin: '0 0 14px', lineHeight: 1.2 }}>
              <CmsEditorialText id="information.sameDay.availabilityHeading" value={DOSTAWA_CONTENT.sameDay.availabilityHeading} section="Dostawa tego samego dnia" label="Dostępność - nagłówek" />
            </h3>
            <div style={{ padding: '18px 20px', background: `${GOLD}0a`, border: `1px solid ${GOLD}33`, display: 'flex', gap: 14, alignItems: 'flex-start' }}>
              <Phone size={14} color={GOLD} style={{ marginTop: 2, flexShrink: 0 }} />
              <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 13, color: DARK, lineHeight: 1.75, margin: 0 }}>
                <CmsEditorialText id="information.sameDay.availabilityIntro" value={DOSTAWA_CONTENT.sameDay.availabilityIntro} section="Dostawa tego samego dnia" label="Dostępność - wprowadzenie" />{' '}
                <a href="tel:+48605030728" style={{ color: GOLD, textDecoration: 'none', fontWeight: 500 }}><CmsEditorialText id="information.sameDay.availabilityCta" value={DOSTAWA_CONTENT.sameDay.availabilityCta} section="Dostawa tego samego dnia" label="Dostępność - kontakt" /></a>.{' '}
                <CmsEditorialText id="information.sameDay.availabilityNote" value={DOSTAWA_CONTENT.sameDay.availabilityNote} section="Dostawa tego samego dnia" label="Dostępność - informacja" multiline />
              </p>
            </div>
          </motion.div>

          {/* 5. Godzina orientacyjna */}
          <motion.div {...fadeUp(0.1)}>
            <div style={{ background: '#fff', padding: 'clamp(28px, 3vw, 40px)', borderLeft: `3px solid ${GOLD}66` }}>
              <SectionLabel text={DOSTAWA_CONTENT.sameDay.importantEyebrow} />
              <h3 style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: 'clamp(1.4rem, 2.2vw, 1.9rem)', fontWeight: 400, color: DARK, margin: '0 0 14px', lineHeight: 1.15 }}>
                <CmsEditorialText id="information.sameDay.importantHeading" value={DOSTAWA_CONTENT.sameDay.importantHeading} section="Dostawa tego samego dnia" label="Ważne — nagłówek" />
              </h3>
              <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 13.5, color: MUTED, lineHeight: 1.85, marginBottom: 16 }}>
                <CmsEditorialText id="information.sameDay.importantBody" value={DOSTAWA_CONTENT.sameDay.importantBody} section="Dostawa tego samego dnia" label="Ważne — opis" multiline />
              </p>
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <AlertCircle size={13} color={GOLD} style={{ marginTop: 2, flexShrink: 0 }} />
                <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 12.5, color: MUTED, lineHeight: 1.75, margin: 0 }}>
                  <CmsEditorialText id="information.sameDay.delayNote" value={DOSTAWA_CONTENT.sameDay.delayNote} section="Dostawa tego samego dnia" label="Informacja o opóźnieniu" multiline />
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* ─── 6. NIESPODZIANKA ───────────────────────────────────────── */

/* ─── 7. CO JEŚLI NIE MA ODBIORCY ────────────────────────────── */
function AbsentSection() {
  return (
    <section style={{ background: '#fff', padding: 'clamp(56px, 7vw, 88px) 0' }}>
      <div style={{ maxWidth: 1370, margin: '0 auto', padding: '0 var(--page-gutter)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 460px), 1fr))', gap: 'clamp(32px, 6vw, 88px)', alignItems: 'start' }}>

          <motion.div {...fadeUp()} style={{ textAlign: 'center' }}>
            <SectionLabel text={DOSTAWA_CONTENT.absent.eyebrow} />
            <h2 style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: 'clamp(2rem, 3.5vw, 3rem)', fontWeight: 400, color: DARK, margin: '0 0 18px', lineHeight: 1.1 }}>
              <CmsEditorialText id="information.absent.heading" value={DOSTAWA_CONTENT.absent.heading} section="Nieobecność odbiorcy" label="Nagłówek" />
            </h2>
            <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 14, color: MUTED, lineHeight: 1.85, marginBottom: 24 }}>
              <CmsEditorialText id="information.absent.intro" value={DOSTAWA_CONTENT.absent.intro} section="Nieobecność odbiorcy" label="Wprowadzenie" multiline />
            </p>
            <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 14, color: MUTED, lineHeight: 1.85, marginBottom: 18 }}>
              <CmsEditorialText id="information.absent.optionsIntro" value={DOSTAWA_CONTENT.absent.optionsIntro} section="Nieobecność odbiorcy" label="Wprowadzenie do możliwości" multiline />
            </p>
            {DOSTAWA_CONTENT.absent.options.map((opt, i) => (
              <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 12 }}>
                <div style={{ width: 22, height: 22, borderRadius: '50%', border: `1px solid ${GOLD}66`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                  <span style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 11, color: GOLD }}>{i + 1}</span>
                </div>
                <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 13.5, color: DARK, lineHeight: 1.7, margin: 0 }}><CmsEditorialText id={`information.absent.options.${i}.text`} value={opt} section="Nieobecność odbiorcy" label={`Element ${i + 1}: tekst`} multiline /></p>
              </div>
            ))}
            <div style={{ marginTop: 20, padding: '14px 18px', background: `${GOLD}0a`, border: `1px solid ${GOLD}33`, display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              <AlertCircle size={13} color={GOLD} style={{ marginTop: 2, flexShrink: 0 }} />
              <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 12.5, color: MUTED, lineHeight: 1.75, margin: 0 }}>
                <CmsEditorialText id="information.absent.leaveWarning" value={DOSTAWA_CONTENT.absent.leaveWarning} section="Nieobecność odbiorcy" label="Informacja o pozostawieniu kwiatów" multiline />
              </p>
            </div>
          </motion.div>

          {/* 8. Nieudana dostawa */}
          <motion.div {...fadeUp(0.1)} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ background: '#fff', padding: 'clamp(24px, 3vw, 36px)', borderTop: `2px solid ${GOLD}55` }}>
              <h3 style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: 'clamp(1.3rem, 2vw, 1.75rem)', fontWeight: 400, color: DARK, margin: '0 0 14px', lineHeight: 1.2 }}>
                <CmsEditorialText id="information.absent.noPickupHeading" value={DOSTAWA_CONTENT.absent.noPickupHeading} section="Nieobecność odbiorcy" label="Brak odbioru — nagłówek" />
              </h3>
              <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 13.5, color: MUTED, lineHeight: 1.85, marginBottom: 14 }}>
                <CmsEditorialText id="information.absent.noPickupBody" value={DOSTAWA_CONTENT.absent.noPickupBody} section="Nieobecność odbiorcy" label="Brak odbioru — opis" multiline />
              </p>
              <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 13.5, color: MUTED, lineHeight: 1.85, margin: 0 }}>
                Jeśli konieczna będzie ponowna próba dostarczenia zamówienia, <strong style={{ color: DARK, fontWeight: 500 }}>ponowna dostawa jest dodatkowo płatna</strong>.
              </p>
            </div>

            {/* 9. Dane odbiorcy */}
            <div style={{ background: '#fff', padding: 'clamp(24px, 3vw, 36px)', borderTop: `2px solid ${GOLD}55` }}>
              <h3 style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: 'clamp(1.3rem, 2vw, 1.75rem)', fontWeight: 400, color: DARK, margin: '0 0 14px', lineHeight: 1.2 }}>
                <CmsEditorialText id="information.absent.recipientDataHeading" value={DOSTAWA_CONTENT.absent.recipientDataHeading} section="Nieobecność odbiorcy" label="Dane odbiorcy — nagłówek" />
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {DOSTAWA_CONTENT.absent.recipientDataItems.map((item, i) => (
                  <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <div style={{ width: 5, height: 5, borderRadius: '50%', background: `${GOLD}99`, flexShrink: 0 }} />
                    <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 13, color: DARK }}><CmsEditorialText id={`information.absent.recipientDataItems.${i}.text`} value={item} section="Nieobecność odbiorcy" label={`Element ${i + 1}: tekst`} multiline /></span>
                  </div>
                ))}
              </div>
              <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 12.5, color: MUTED, lineHeight: 1.75, margin: '14px 0 0' }}>
                <CmsEditorialText id="information.absent.recipientDataFooter" value={DOSTAWA_CONTENT.absent.recipientDataFooter} section="Nieobecność odbiorcy" label="Treść sekcji" />
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* ─── 10. ODBIÓR OSOBISTY ────────────────────────────────────── */
function PickupSection() {
  return (
    <section style={{ background: '#fff', padding: 'clamp(56px, 7vw, 88px) 0' }}>
      <div style={{ maxWidth: 1370, margin: '0 auto', padding: '0 var(--page-gutter)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 460px), 1fr))', gap: 'clamp(32px, 6vw, 88px)', alignItems: 'start' }}>

          <motion.div {...fadeUp()} style={{ textAlign: 'center' }}>
            <SectionLabel text={DOSTAWA_CONTENT.pickup.eyebrow} />
            <h2 style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: 'clamp(2rem, 3.5vw, 3rem)', fontWeight: 400, color: DARK, margin: '0 0 16px', lineHeight: 1.1 }}>
              {DOSTAWA_CONTENT.pickup.heading}
            </h2>
            <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 14, color: MUTED, lineHeight: 1.85, marginBottom: 20 }}>
              {DOSTAWA_CONTENT.pickup.body}
            </p>
            <div style={{ display: 'inline-flex', gap: 10, alignItems: 'center', padding: '10px 18px', background: `${GOLD}0d`, border: `1px solid ${GOLD}44` }}>
              <CheckCircle2 size={13} color={GOLD} />
              <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 12, letterSpacing: '0.1em', color: DARK, fontWeight: 500 }}>{DOSTAWA_CONTENT.pickup.freeBadge}</span>
            </div>
            <div style={{ marginTop: 24, padding: '18px 20px', background: '#fff', borderLeft: `2px solid ${GOLD}55` }}>
              <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 9.5, letterSpacing: '0.2em', textTransform: 'uppercase', color: MUTED, margin: '0 0 10px' }}>{DOSTAWA_CONTENT.pickup.hoursLabel}</p>
              {DOSTAWA_CONTENT.pickup.hoursRows.map((row, i, arr) => (
                <p key={row.label} style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 19, color: DARK, margin: i === arr.length - 1 ? 0 : '0 0 6px' }}>
                  {row.label}: <strong style={{ fontWeight: 400, color: GOLD }}>{row.value}</strong>
                </p>
              ))}
            </div>
            <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 12.5, color: MUTED, lineHeight: 1.75, margin: '16px 0 0' }}>
              {DOSTAWA_CONTENT.pickup.footerNote}
            </p>
          </motion.div>

          <motion.div {...fadeUp(0.1)} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {DOSTAWA_CONTENT.pickup.stores.map((s, i) => (
              <div key={i} style={{ background: '#fff', padding: 'clamp(22px, 3vw, 32px)', borderTop: `2px solid ${GOLD}55` }}>
                <div style={{ width: 38, height: 38, borderRadius: '50%', background: `${GOLD}12`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
                  <Store size={15} color={GOLD} />
                </div>
                <h3 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 20, fontWeight: 400, color: DARK, margin: '0 0 10px' }}>{s.name.replace(/\u2014/g, '-')}</h3>
                <a href={s.href} target="_blank" rel="noopener noreferrer"
                  style={{ display: 'flex', gap: 8, alignItems: 'center', fontFamily: 'DM Sans, sans-serif', fontSize: 13, color: MUTED, textDecoration: 'none', marginBottom: 6 }}
                >
                  <MapPin size={11} color={GOLD} />
                  {s.address}
                </a>
                <a href={`tel:${s.phone.replace(/\s/g, '')}`}
                  style={{ display: 'flex', gap: 8, alignItems: 'center', fontFamily: 'DM Sans, sans-serif', fontSize: 13, color: MUTED, textDecoration: 'none' }}
                >
                  <Phone size={11} color={GOLD} />
                  {s.phone}
                </a>
              </div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* ─── 11. JAK ZAMÓWIĆ ────────────────────────────────────────── */
function HowSection() {
  return (
    <section style={{ background: '#fff', padding: 'clamp(56px, 7vw, 88px) 0' }}>
      <div style={{ maxWidth: 1370, margin: '0 auto', padding: '0 var(--page-gutter)' }}>
        <motion.div {...fadeUp()} style={{ textAlign: 'center', marginBottom: 'clamp(44px, 5vw, 60px)' }}>
          <SectionLabel text={DOSTAWA_CONTENT.how.eyebrow} />
          <h2 style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: 'clamp(2rem, 3.5vw, 3rem)', fontWeight: 400, color: DARK, margin: 0, lineHeight: 1.1 }}>
            <CmsEditorialText id="information.how.heading" value={DOSTAWA_CONTENT.how.heading} section="Jak zamawiać" label="Nagłówek" />
          </h2>
          <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 14, color: MUTED, lineHeight: 1.85, maxWidth: 680, margin: '18px auto 0' }}>
            <CmsEditorialText id="information.how.intro" value={DOSTAWA_CONTENT.how.intro} section="Jak zamawiać" label="Wprowadzenie" multiline />{' '}
            <a href="/bukiety/" style={{ color: GOLD, textDecoration: 'underline' }}><CmsEditorialText id="information.how.bouquetLinkLabel" value={DOSTAWA_CONTENT.how.bouquetLinkLabel} section="Jak zamawiać" label="Link do bukietów" /></a>{' '}
            <CmsEditorialText id="information.how.linkJoin" value={DOSTAWA_CONTENT.how.linkJoin} section="Jak zamawiać" label="Łącznik linków" />{' '}
            <a href="/flower-boxy/" style={{ color: GOLD, textDecoration: 'underline' }}><CmsEditorialText id="information.how.flowerBoxLinkLabel" value={DOSTAWA_CONTENT.how.flowerBoxLinkLabel} section="Jak zamawiać" label="Link do flower boxów" /></a><CmsEditorialText id="information.how.introEnd" value={DOSTAWA_CONTENT.how.introEnd} section="Jak zamawiać" label="Zakończenie wprowadzenia" multiline />
          </p>
        </motion.div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 160px), 1fr))', gap: 'clamp(16px, 3vw, 0px)', position: 'relative' }}>
          {DOSTAWA_CONTENT.how.steps.map((s, i) => (
            <motion.div key={s.num} {...fadeUp(i * 0.09)} style={{ padding: '0 clamp(8px, 2vw, 24px)', textAlign: 'center' }}>
              <div style={{ width: 54, height: 54, borderRadius: '50%', margin: '0 auto 22px', border: `1px solid ${GOLD}55`, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', zIndex: 1 }}>
                <span style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 16, color: GOLD }}>{s.num}</span>
              </div>
              <h3 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(16px, 1.8vw, 20px)', fontWeight: 400, color: DARK, margin: '0 0 10px', lineHeight: 1.25 }}><CmsEditorialText id={`information.how.steps.${i}.title`} value={s.title} section="Jak zamawiać" label={`Element ${i + 1}: tytuł`} /></h3>
              <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 12.5, color: MUTED, lineHeight: 1.75, margin: 0 }}><CmsEditorialText id={`information.how.steps.${i}.desc`} value={s.desc} section="Jak zamawiać" label={`Element ${i + 1}: opis`} multiline /></p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── 12. FAQ ────────────────────────────────────────────────── */
function FaqItem({ item, index }: { item: FaqEntry & { cmsKey: string }; index: number }) {
  const [open, setOpen] = useState(false);
  return (
    <motion.div {...fadeUp(index * 0.025)} style={{ borderBottom: `1px solid ${LINE}` }}>
      <button
        onClick={() => setOpen(v => !v)}
        style={{
          width: '100%', textAlign: 'left', padding: 'clamp(15px, 1.8vw, 19px) 0',
          background: 'none', border: 'none', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16,
        }}
      >
        <span style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(16px, 1.7vw, 19px)', fontWeight: 400, color: DARK, lineHeight: 1.3 }}>
          <span data-cms-text={`${item.cmsKey}.q`}>{item.q}</span>
        </span>
        <ChevronDown size={15} color={GOLD} style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.25s', flexShrink: 0 }} />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.28, ease: [0.4, 0, 0.2, 1] }}
            style={{ overflow: 'hidden' }}
          >
            <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 13.5, color: MUTED, lineHeight: 1.85, margin: '0 0 16px', paddingRight: 28 }}>
              <span data-cms-text={`${item.cmsKey}.a`}>{item.a}</span>
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function GroupLabel({ label, icon }: { label: string; icon: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: 'clamp(20px,2.5vw,28px) 0 clamp(12px,1.5vw,16px)', borderBottom: `1px solid ${LINE}` }}>
      <div style={{ width: 28, height: 28, borderRadius: '50%', border: `1px solid ${GOLD}55`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        {icon}
      </div>
      <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 9.5, letterSpacing: '0.3em', textTransform: 'uppercase', color: GOLD }}>
        {label}
      </span>

    </div>
  );
}

function FaqSection() {
  const cmsFaq = useCmsFaq(DOSTAWA_CONTENT.faq.items);
  const deliveryItems = cmsFaq.items.filter(f => f.group === 'delivery');
  const paymentItems  = cmsFaq.items.filter(f => f.group === 'payment');
  return (
    <section style={{ background: '#fff', padding: 'clamp(56px, 7vw, 88px) 0' }}>

      <CmsSchema fields={cmsFaq.fields} />
      <div style={{ maxWidth: 800, margin: '0 auto', padding: '0 clamp(24px, 5vw, 64px)' }}>
        <motion.div {...fadeUp()} style={{ textAlign: 'center', marginBottom: 'clamp(40px, 5vw, 56px)' }}>
          <SectionLabel text={DOSTAWA_CONTENT.faq.eyebrow} />
          <h2 style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: 'clamp(2rem, 3.5vw, 3rem)', fontWeight: 400, color: DARK, margin: 0, lineHeight: 1.1 }}>
            <CmsEditorialText id="information.faq.heading" value={DOSTAWA_CONTENT.faq.heading} section="Pytania i odpowiedzi" label="Nagłówek" />
          </h2>
        </motion.div>

        <GroupLabel label={DOSTAWA_CONTENT.faq.deliveryGroupLabel} icon={<Truck size={12} color={GOLD} />} />
        {deliveryItems.map((item, i) => <FaqItem key={i} item={item} index={i} />)}

        <GroupLabel label={DOSTAWA_CONTENT.faq.paymentGroupLabel} icon={<Shield size={12} color={GOLD} />} />
        {paymentItems.map((item, i) => <FaqItem key={i + deliveryItems.length} item={item} index={i} />)}

        <div style={{ marginTop: 40, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <a href="/kontakt/"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 10, fontFamily: 'DM Sans, sans-serif', fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#fff', background: DARK, padding: '13px 28px', textDecoration: 'none' }}>
            <MessageCircle size={13} /> <CmsEditorialText id="information.faq.contactCtaLabel" value={DOSTAWA_CONTENT.faq.contactCtaLabel} section="Pytania i odpowiedzi" label="Tekst przycisku" />
          </a>
          <a href="/faq/"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 10, fontFamily: 'DM Sans, sans-serif', fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', color: DARK, border: `1px solid rgba(42,31,20,0.22)`, padding: '13px 28px', textDecoration: 'none' }}>
            <HelpCircle size={13} /> <CmsEditorialText id="information.faq.helpCenterCtaLabel" value={DOSTAWA_CONTENT.faq.helpCenterCtaLabel} section="Pytania i odpowiedzi" label="Tekst przycisku" />
          </a>
        </div>
      </div>
    </section>
  );
}

/* ─── 13. PŁATNOŚCI ──────────────────────────────────────────── */

/* Separator rozdziałów: Dostawa → Płatności */
function ChapterDivider() {
  return (
    <div style={{ background: '#fff', padding: 'clamp(20px,2.5vw,28px) 0' }}>
      <div style={{ maxWidth: 1370, margin: '0 auto', paddingInline: 'var(--page-gutter)', display: 'flex', alignItems: 'center', gap: 20 }}>
        <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 9, letterSpacing: '0.45em', textTransform: 'uppercase', color: `${GOLD}99`, whiteSpace: 'nowrap' }}>
          <CmsEditorialText id="information.chapterDivider.left" value={DOSTAWA_CONTENT.chapterDivider.left} section="Płatności" label="Treść sekcji" />
        </span>

        <span style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(1.1rem,1.8vw,1.5rem)', fontWeight: 400, color: GOLD, whiteSpace: 'nowrap', letterSpacing: '0.04em' }}>
          <CmsEditorialText id="information.chapterDivider.center" value={DOSTAWA_CONTENT.chapterDivider.center} section="Płatności" label="Treść sekcji" />
        </span>

        <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 9, letterSpacing: '0.45em', textTransform: 'uppercase', color: `${GOLD}99`, whiteSpace: 'nowrap' }}>
          <CmsEditorialText id="information.chapterDivider.right" value={DOSTAWA_CONTENT.chapterDivider.right} section="Płatności" label="Treść sekcji" />
        </span>
      </div>
    </div>
  );
}

const PAYMENT_ICON_RENDERERS: Record<string, () => React.ReactNode> = {
  card: () => (
    <div style={{ width: 48, height: 32, background: DARK, borderRadius: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <svg width="26" height="18" viewBox="0 0 28 20" fill="none" aria-hidden><rect x="0.5" y="0.5" width="27" height="19" rx="2.5" stroke="rgba(255,255,255,0.25)"/><rect y="5" width="28" height="5" fill="rgba(255,255,255,0.2)"/><rect x="4" y="13" width="8" height="3" rx="1" fill="rgba(255,255,255,0.6)"/></svg>
    </div>
  ),
  blik: () => (
    <div style={{ width: 48, height: 32, background: '#fff', borderRadius: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, overflow: 'hidden' }}>
      <img src={`${BASE}/logo-blik.png`} alt="BLIK" style={{ height: 20, objectFit: 'contain', display: 'block' }} />
    </div>
  ),
  p24: () => (
    <div style={{ width: 48, height: 32, background: '#fff', borderRadius: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, overflow: 'hidden' }}>
      <img src={`${BASE}/logo-p24.png`} alt="Przelewy24" style={{ height: 20, objectFit: 'contain', display: 'block' }} />
    </div>
  ),
};

function PaymentSection() {
  return (
    <section id="platnosci" style={{ background: '#fff', padding: 'clamp(56px,7vw,88px) 0' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 clamp(24px,5vw,64px)' }}>

        {/* Header */}
        <motion.div {...fadeUp()} style={{ marginBottom: 'clamp(40px,5vw,56px)' }}>
          <SectionLabel text={DOSTAWA_CONTENT.payment.eyebrow} />
          <h2 style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: 'clamp(2rem,3.5vw,3rem)', fontWeight: 400, color: DARK, margin: 0, lineHeight: 1.1 }}>
            <CmsEditorialText id="information.payment.heading" value={DOSTAWA_CONTENT.payment.heading} section="Opis płatności" label="Nagłówek" />
          </h2>
        </motion.div>

        {/* Grid: metody | bezpieczeństwo */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 380px), 1fr))', gap: 1, background: LINE, marginBottom: 'clamp(40px,5vw,64px)' }}>

          {/* Metody */}
          <motion.div {...fadeUp()} style={{ background: '#fff', padding: 'clamp(28px,3.5vw,44px)', display: 'flex', flexDirection: 'column', gap: 1 }}>
            <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 9.5, letterSpacing: '0.28em', textTransform: 'uppercase', color: MUTED, margin: '0 0 20px' }}><CmsEditorialText id="information.payment.methodsLabel" value={DOSTAWA_CONTENT.payment.methodsLabel} section="Opis płatności" label="Tekst przycisku" /></p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 1, background: LINE, margin: '0 -1px' }}>
              {DOSTAWA_CONTENT.payment.methods.map(({ iconKey, title, desc }) => (
                <div key={title} style={{ background: '#fff', padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 16 }}>
                  {PAYMENT_ICON_RENDERERS[iconKey]()}
                  <div style={{ flex: 1 }}>
                    <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 13.5, fontWeight: 500, color: DARK, margin: '0 0 2px' }}>{title}</p>
                    <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 12, color: MUTED, margin: 0 }}>{desc}</p>
                  </div>
                  <CheckCircle2 size={14} color={GOLD} style={{ flexShrink: 0 }} />
                </div>
              ))}
            </div>
            <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 12.5, color: MUTED, lineHeight: 1.75, margin: '20px 0 0' }}>
              Płatności obsługuje <strong style={{ color: DARK, fontWeight: 500 }}>Stripe</strong>. Twoje dane finansowe nigdy nie trafiają na nasze serwery.
            </p>
          </motion.div>

          {/* Bezpieczeństwo + Proces */}
          <motion.div {...fadeUp(0.1)} style={{ background: '#fff', padding: 'clamp(28px,3.5vw,44px)', display: 'flex', flexDirection: 'column', gap: 32 }}>

            {/* Bezpieczeństwo */}
            <div>
              <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 9.5, letterSpacing: '0.28em', textTransform: 'uppercase', color: MUTED, margin: '0 0 16px' }}><CmsEditorialText id="information.payment.securityLabel" value={DOSTAWA_CONTENT.payment.securityLabel} section="Opis płatności" label="Tekst przycisku" /></p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {DOSTAWA_CONTENT.payment.securityItems.map((item, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                    <Shield size={13} color={GOLD} style={{ flexShrink: 0, marginTop: 2 }} />
                    <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 13.5, color: DARK, margin: 0, lineHeight: 1.55 }}><CmsEditorialText id={`information.payment.securityItems.${i}.text`} value={item} section="Opis płatności" label={`Element ${i + 1}: tekst`} multiline /></p>
                  </div>
                ))}
              </div>
            </div>

            {/* Divider */}

            {/* Proces — 3 kroki inline */}
            <div>
              <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 9.5, letterSpacing: '0.28em', textTransform: 'uppercase', color: MUTED, margin: '0 0 16px' }}><CmsEditorialText id="information.payment.processLabel" value={DOSTAWA_CONTENT.payment.processLabel} section="Opis płatności" label="Tekst przycisku" /></p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {DOSTAWA_CONTENT.payment.processSteps.map(step => (
                  <div key={step.n} style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                    <div style={{ width: 26, height: 26, borderRadius: '50%', border: `1px solid ${GOLD}55`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <span style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 12, color: GOLD }}>{step.n}</span>
                    </div>
                    <div>
                      <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 13.5, fontWeight: 500, color: DARK, margin: '0 0 2px' }}>{step.title}</p>
                      <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 12.5, color: MUTED, margin: 0, lineHeight: 1.55 }}>{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>

        {/* Problem z płatnością */}
        <motion.div {...fadeUp(0.05)} style={{ padding: 'clamp(20px,2.5vw,28px) clamp(20px,2.5vw,32px)', background: '#fff', border: `1px solid ${LINE}`, display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <AlertCircle size={15} color={GOLD} style={{ flexShrink: 0, marginTop: 2 }} />
          <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: 13.5, color: DARK, lineHeight: 1.7, margin: 0, flex: 1, minWidth: 200 }}>
            <strong style={{ fontWeight: 500 }}>Płatność nie przeszła lub środki pobrane bez potwierdzenia?</strong>{' '}
            <a href="/kontakt/" style={{ color: GOLD, textDecoration: 'none', fontWeight: 500 }}>Skontaktuj się z nami</a>{' '}
            - wyjaśnimy sytuację i pomożemy rozwiązać problem.
          </p>
        </motion.div>

      </div>
    </section>
  );
}

/* ─── Page ───────────────────────────────────────────────────── */
export default function DostawaPage() {
  usePrerenderReady();
  useSeo(PAGE_SEO.dostawa);
  return (
    <div className="min-h-[100dvh] bg-background">
      <main>
        <Hero />
        <SameDaySection />
        <AbsentSection />
        <PickupSection />
        <HowSection />
        <ChapterDivider />
        <PaymentSection />
        <FaqSection />
      </main>
      <Footer />
    </div>
  );
}
